// frontend/src/services/PriceService.js

class PriceService {
  constructor() {
    if (PriceService.instance) return PriceService.instance;

    this.socket = null;
    this.subscriptions = new Map(); 
    this.prices = new Map();         
    this.lastDayCloses = new Map();  
    this.marketStatuses = new Map(); 
    this.subscribedSymbols = new Set(); 
    
    this.PRICE_API_URL = process.env.REACT_APP_PRICE_API_URL;
    this.API_KEY = process.env.REACT_APP_PRICE_API_KEY;

    this.connect();
    PriceService.instance = this;
  }

  connect() {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;

    const wsUrl = `${this.PRICE_API_URL.replace('http', 'ws')}/ws?apiKey=${this.API_KEY}`;
    this.socket = new WebSocket(wsUrl);

    this.socket.onopen = () => {
      console.log('PriceService: Connected');
      this.subscribedSymbols.clear();
      this.syncSubscriptions();
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.symbol) {
          const symbolLower = message.symbol.toLowerCase();
          
          // New price server format: { symbol, data, last_day_close, market_status }
          // data contains the candle { time, open, high, low, close }
          const price = message.data ? message.data.close : null;
          const lastDayClose = message.last_day_close;
          const marketStatus = message.market_status;

          if (price !== null) this.prices.set(symbolLower, price);
          if (lastDayClose !== undefined) this.lastDayCloses.set(symbolLower, lastDayClose);
          if (marketStatus !== undefined) this.marketStatuses.set(symbolLower, marketStatus);

          this.notify(symbolLower, { 
            price: this.prices.get(symbolLower), 
            lastDayClose: this.lastDayCloses.get(symbolLower),
            marketStatus: this.marketStatuses.get(symbolLower)
          });
        }
      } catch (error) {
        console.error('PriceService: Parse Error', error);
      }
    };

    this.socket.onclose = () => {
      console.log('PriceService: Disconnected. Retrying...');
      this.subscribedSymbols.clear();
      setTimeout(() => this.connect(), 5000);
    };
  }

  syncSubscriptions() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    const currentSymbols = Array.from(this.subscriptions.keys());
    
    currentSymbols.forEach(symLower => {
      if (!this.subscribedSymbols.has(symLower)) {
        this.socket.send(JSON.stringify({ action: 'subscribe', symbol: symLower }));
        this.subscribedSymbols.add(symLower);
      }
    });

    this.subscribedSymbols.forEach(symLower => {
      if (!this.subscriptions.has(symLower)) {
        this.socket.send(JSON.stringify({ action: 'unsubscribe', symbol: symLower }));
        this.subscribedSymbols.delete(symLower);
      }
    });
  }

  notify(symbolLower, data) {
    const callbacks = this.subscriptions.get(symbolLower);
    if (callbacks) {
      callbacks.forEach(cb => cb(data));
    }
  }

  subscribe(symbol, callback) {
    if (!symbol) return;
    const symbolLower = symbol.toLowerCase();
    if (!this.subscriptions.has(symbolLower)) {
      this.subscriptions.set(symbolLower, new Set());
    }
    this.subscriptions.get(symbolLower).add(callback);
    
    const price = this.prices.get(symbolLower);
    const lastDayClose = this.lastDayCloses.get(symbolLower);
    const marketStatus = this.marketStatuses.get(symbolLower);
    
    if (price !== undefined || lastDayClose !== undefined || marketStatus !== undefined) {
      callback({ price, lastDayClose, marketStatus });
    }

    this.syncSubscriptions();
  }

  unsubscribe(symbol, callback) {
    if (!symbol) return;
    const symbolLower = symbol.toLowerCase();
    if (this.subscriptions.has(symbolLower)) {
      this.subscriptions.get(symbolLower).delete(callback);
      if (this.subscriptions.get(symbolLower).size === 0) {
        this.subscriptions.delete(symbolLower);
        this.syncSubscriptions();
      }
    }
  }
}

const priceServiceInstance = new PriceService();
export default priceServiceInstance;
